<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <!-- Left Panel: Others Info -->
        <v-col md="3" cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Others Info
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="submitForm(1)">
                  <v-row class="pa-2">
                    <v-col cols="12">
                      <!-- PO -->
                      <v-autocomplete
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        v-model="po_id.value.value"
                        :items="poList"
                        :item-title="item => item.code || `PO-${item.id}`"
                        :item-value="item => item.id"
                        :error-messages="po_id.errorMessage"
                        :disabled="!isEntryMode"
                        @update:model-value="onPoChange"
                        autocomplete="off"
                        clearable
                      >
                        <template #label>
                          PO <span class="text-red-accent-4">*</span>
                        </template>
                      </v-autocomplete>

                      <!-- Sewing Line -->
                      <v-text-field
                        v-model="line.value.value"
                        variant="outlined"
                        label="Sewing Line"
                        density="compact"
                        :error-messages="line.errorMessage"
                      ></v-text-field>

                      <!-- Sewing Date -->
                      <v-date-input
                        label="Sewing Date"
                        density="compact"
                        prepend-inner-icon="mdi-calendar"
                        prepend-icon=""
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        v-model="sewing_date.value.value"
                        :error-messages="sewing_date.errorMessage"
                        autocomplete="off"
                        clearable
                      ></v-date-input>

                      <input type="hidden" v-model="sewing_id.value" />
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>

        <v-col md="9" cols="12">
          <v-expansion-panels v-model="panel2">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Sewing Details
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="submitForm(1)">
                  <!-- Details Table -->
                  <v-row class="pt-0">
                    <v-col cols="12">
                      <v-table density="compact" :loading="loading" fixed-header class="table-dotted-border-bottom mt-2">
                        <thead>
                          <tr>
                            <th class="text-center" style="width:5%">SL</th>
                            <th class="text-left" style="width:30%">Color</th>
                            <th class="text-left" style="width:30%">Size</th>
                            <th class="text-center" style="width:20%">Quantity</th>
                            <th class="text-center" style="width:15%">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(detail, index) in detailRows" :key="index">
                            <td class="text-center py-1">{{ index + 1 }}</td>
                            <td class="py-1">
                              <v-select
                                v-model="detail.color_id"
                                :items="colorList"
                                item-title="name"
                                item-value="id"
                                variant="outlined"
                                density="compact"
                                bg-color="grey-lighten-4"
                                hide-details
                                readonly="true"
                                disabled="true"
                                :error-messages="detailErrors[index]?.color_id"
                              ></v-select>
                            </td>
                            <td class="py-1">
                              <v-select
                                v-model="detail.size_id"
                                :items="sizeList"
                                item-title="name"
                                item-value="id"
                                variant="outlined"
                                density="compact"
                                bg-color="grey-lighten-4"
                                hide-details
                                readonly="true"
                                disabled="true"
                                :error-messages="detailErrors[index]?.size_id"
                              ></v-select>
                            </td>
                            <td class="text-center py-1">
                              <v-text-field
                                v-model.number="detail.sewn_qty"
                                type="number"
                                variant="outlined"
                                density="compact"
                                bg-color="grey-lighten-4"
                                style="width:100%"
                                hide-details
                                :error-messages="detailErrors[index]?.sewn_qty"
                              ></v-text-field>
                            </td>
                            <td class="text-left py-1">
                              <v-btn
                                icon
                                variant="tonal"
                                size="small"
                                color="red"
                                @click="removeDetailRow(index)"
                                v-if="detailRows.length > 1"
                              >
                                <v-icon>mdi-close</v-icon>
                              </v-btn>

                              <v-btn
                                icon
                                variant="tonal"
                                size="small"
                                color="blue"
                                @click="addDetailRow"
                                v-if="index === detailRows.length - 1"
                              >
                                <v-icon>mdi-plus</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                          <tr v-if="detailRows.length === 0">
                            <td colspan="5" class="text-center text-red">No sewing details found</td>
                          </tr>
                        </tbody>
                      </v-table>
                    </v-col>
                  </v-row>

                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-row class="pt-2">
                      <v-col cols="8"></v-col>
                      <v-col cols="4">
                        <v-layout class="justify-end">
                          <v-btn class="bg-grey-darken-1 me-2" @click="submitForm(0)" :loading="loading">Draft</v-btn>
                          <v-btn class="bg-primary" @click="submitForm(1)" :loading="loading">
                            {{ isEntryMode ? "Save" : "Update" }}
                          </v-btn>
                        </v-layout>
                      </v-col>
                    </v-row>
                  </v-card-actions>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { ref, watch } from "vue";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

export default {
  name: "SewingInputForm",

  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const loading = ref(false);
    const isEntryMode = ref(true);
    const panel = ref([0]);
    const panel2 = ref([0]);

    const detailRows = ref([{ color_id: "", size_id: "", sewn_qty: 0 }]);
    const detailErrors = ref({});

    // Form Fields
    const { handleSubmit, resetForm, setFieldValue } = useForm({
      validationSchema: yup.object({
        po_id: yup.number().required().label("PO"),
        sewing_date: yup.date().required().label("Sewing Date"),
      }),
    });

    const po_id = useField("po_id");
    const sewing_date = useField("sewing_date");
    const line = useField("line");
    const sewing_id = useField("sewing_id");
    const status = ref(1);

    // Add / Remove detail rows
    const addDetailRow = () => detailRows.value.push({ color_id: "", size_id: "", sewn_qty: 0 });
    const removeDetailRow = (index) => {
      if (detailRows.value.length > 1) {
        detailRows.value.splice(index, 1);
        delete detailErrors.value[index];
      }
    };

    // Validate details
    const validateDetails = () => {
      detailErrors.value = {};
      let valid = true;
      detailRows.value.forEach((d, i) => {
        const err = {};
        if (!d.color_id) { err.color_id = "Required"; valid = false; }
        if (!d.size_id) { err.size_id = "Required"; valid = false; }
        if (!d.sewn_qty || d.sewn_qty <= 0) { err.sewn_qty = "Must be >0"; valid = false; }
        if (Object.keys(err).length) detailErrors.value[i] = err;
      });
      return valid;
    };

    const onPoChange = async (poId) => {
      if (!poId) return;
      loading.value = true;
      try {
        const res = await store.dispatch("getProductionOrderById", { id: poId });
        const breakdowns = [];
        res.data.data.items.forEach(item => {
          item.breakdowns.forEach(bd => {
            if (bd.order_quantity > bd.sewn_qty) {
              breakdowns.push({
                color_id: bd.color_id,
                size_id: bd.size_id,
                sewn_qty: bd.order_quantity - bd.sewn_qty,
                production_order_item_id: item.id,
                production_order_breakdown_id: bd.id,
              });
            }
          });
        });
        detailRows.value = breakdowns.length ? breakdowns : [{ color_id: "", size_id: "", sewn_qty: 0 }];
      } catch (err) {
        toast.error("Failed to load production order details");
        detailRows.value = [{ color_id: "", size_id: "", sewn_qty: 0 }];
      } finally { loading.value = false; }
    };

    const submitForm = (st) => {
      status.value = st;
      submit();
    };

    const submit = handleSubmit((values) => {
      //if (!validateDetails()) return toast.error("Please fill all detail fields correctly");

      const payload = {
        production_order_id: values.po_id,
        sewing_date: formattedDate(values.sewing_date),
        line: values.line,
        status: status.value,
        details: detailRows.value.map(d => ({
          production_order_item_id: d.production_order_item_id,
          production_order_breakdown_id: d.production_order_breakdown_id,
          color_id: d.color_id,
          size_id: d.size_id,
          sewn_qty: Number(d.sewn_qty),
        })),
      };
      

      loading.value = true;
      const action = values.sewing_id ? "updateSewingInput" : "saveSewingInput";
      const param = values.sewing_id ? { id: values.sewing_id, data: payload } : payload;
      

      store.dispatch(action, param)
        .then(() => {
          toast.success(values.sewing_id ? "Updated Successfully" : "Created Successfully");
          router.push("/bhp/sewing/input-record");
        })
        .catch(err => toast.error(err.response?.data?.message || "Something went wrong"))
        .finally(() => loading.value = false);
    });

    // Load data if editing
    const loadSewingInput = async (id) => {
      if (!id) return;
      loading.value = true;
      try {
        const res = await store.dispatch("getSewingInputById", { id });
        const data = res.data.data;
        isEntryMode.value = false;

        po_id.value.value = data.production_order_id;
        sewing_date.value.value = data.sewing_date;
        line.value.value = data.line;
        sewing_id.value.value = data.id;

        detailRows.value = data.details.length ? data.details.map(d => ({
          color_id: d.color_id,
          size_id: d.size_id,
          sewn_qty: Number(d.sewn_qty),
          production_order_item_id: d.production_order_item_id,
          production_order_breakdown_id: d.production_order_breakdown_id,
        })) : [{ color_id: "", size_id: "", sewn_qty: 0 }];
      } catch (err) {
        toast.error("Failed to load sewing input");
        router.push("/bhp/sewing/input-record");
      } finally { loading.value = false; }
    };

    watch(() => route.params.id, (id) => loadSewingInput(id), { immediate: true });

    return {
      panel,
      panel2,
      po_id,
      sewing_date,
      line,
      sewing_id,
      detailRows,
      detailErrors,
      loading,
      isEntryMode,
      addDetailRow,
      removeDetailRow,
      onPoChange,
      submitForm,
    };
  },

  computed: {
    poList() { return this.$store.getters.getProductionOrderList || []; },
    colorList() { return this.$store.getters.getProductionColorList || []; },
    sizeList() { return this.$store.getters.getProductionSizeList || []; },
  },

  created() {
    this.$store.dispatch("getProductionOrderList");
    if (!this.$store.getters.getProductionColorList.length) this.$store.dispatch("getProductionColorList");
    if (!this.$store.getters.getProductionSizeList.length) this.$store.dispatch("getProductionSizeList");
  },
};
</script>
